<script setup lang="ts">
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import type { InputType, InputSizeType, TextAlignType } from '#imports';

interface IProps {
    label?: string;
    clearable?: boolean;
    description?: string;
    disabled?: boolean;
    invalid?: boolean;
    success?: boolean;
    errorText?: string;
    validation?: (value: string | number | null) => boolean;
    icon?: IconDefinition;
    placeholder?: string;
    min?: number;
    max?: number;
    minLength?: number;
    maxLength?: number;
    autocomplete?: string;
    textArea?: boolean;
    resizable?: boolean;
    readonly?: boolean;
    fullWidth?: boolean;
    required?: boolean;
    maxPrice?: number;
    disableConvertPersianDigit?: boolean;
    type?: InputType;
    inputId?: string;
    dir?: 'rtl' | 'ltr';
    size?: InputSizeType;
    hideDescription?: boolean;
    align?: TextAlignType;
    flatRight?: boolean;
    flatLeft?: boolean;
    autofocus?: boolean;
    requiredError?: string;
    scrollToContainer?: boolean;
}
const {
    label = '',
    clearable,
    description = '',
    disabled,
    invalid,
    success,
    validation,
    icon,
    placeholder = '',
    min = Infinity,
    max = Infinity,
    minLength = 0,
    maxLength = Infinity,
    autocomplete = 'on',
    textArea,
    resizable,
    readonly,
    fullWidth,
    required,
    maxPrice = Infinity,
    disableConvertPersianDigit,
    type = 'text',
    inputId = '',
    dir = 'rtl',
    size = InputSize.dense,
    autofocus,
    errorText,
    align,
    requiredError,
} = defineProps<IProps>();
const emit = defineEmits<{
    click: [event: MouseEvent];
    'click:icon': [event: MouseEvent];
    submit: [];
    keypress: [event: KeyboardEvent];
    focus: [];
    keydown: [event: KeyboardEvent];
    clear: [];
    blur: [];
    search: [value: string];
}>();
const input = ref<HTMLInputElement | HTMLTextAreaElement>();
const [modelValue, modelModifiers] = defineModel<string | number | null>({
    default: '',
    get(mValue) {
        if (type === 'money') {
            const value = mValue ? mValue.toString() : null;
            if (value && Number(value.replace(/\D/g, ''))) {
                return formatMoney(value.replace(/\D/g, ''), {
                    includeUnit: false,
                    expression: 'floor',
                });
            } else {
                return null;
            }
        }
        return mValue;
    },
    set(value) {
        if (type === 'money') {
            const nValue =
                Number(
                    convertToEnglishDigit(value?.toString() || '').replace(
                        /\D/g,
                        ''
                    )
                ) * 10;
            if (value && nValue) {
                if (nValue <= maxPrice) {
                    return nValue;
                } else {
                    nextTick(() => {
                        if (input.value) {
                            input.value.value = formatMoney(value, {
                                includeUnit: false,
                                expression: 'floor',
                            });
                        }
                    });
                }
            } else {
                if (input.value) {
                    input.value.value = '';
                }
                return 0;
            }
        } else if (type === 'number') {
            const nValue = convertToEnglishDigit(
                value?.toString() || ''
            ).replace(/\D/g, '');
            if (nValue) {
                return modelModifiers?.num ? +nValue : nValue;
            } else {
                if (input.value) {
                    input.value.value = '';
                }
                return '';
            }
        } else {
            return !disableConvertPersianDigit
                ? convertToEnglishDigit(value?.toString() || '')
                : value;
        }
    },
});

const localDesc = computed(() => {
    if (localInvalid.value) {
        if (required && !modelValue.value) {
            return requiredError || 'این فیلد نمی‌تواند خالی باشد.';
        } else if (errorText) {
            return errorText;
        } else {
            return 'مقدار واردشده معتبر نیست.';
        }
    } else {
        return description;
    }
});

const localInvalid = computed(() => {
    if (validation) {
        return invalid && !validation(modelValue.value);
    } else {
        return invalid;
    }
});
const computedType = computed<InputType>(() => {
    switch (type) {
        case 'number':
        case 'money':
        case 'card':
            return 'text';
        default:
            return type;
    }
});
const inputMode = computed(() => {
    switch (type) {
        case 'number':
        case 'money':
        case 'card':
            return 'decimal';
        case 'tel':
            return 'tel';
        default:
            return 'text';
    }
});
const computedId = computed(() => {
    if (inputId) {
        return { id: inputId };
    } else {
        return {};
    }
});
const localLabel = computed(() => {
    if (!required && label) {
        return `${label} (اختیاری)`;
    } else {
        return label;
    }
});
const localPlaceholder = computed(() => {
    if (!required && !label && placeholder) {
        return `${placeholder} (اختیاری)`;
    } else {
        return placeholder;
    }
});
const showClear = computed(() => clearable && !!modelValue.value);
const onBlur = () => emit('blur');
const focus = () => input.value?.focus();
const blur = () => input.value?.blur();
const clear = () => {
    modelValue.value = '';
    emit('clear');
};
onMounted(() => {
    if (autofocus) {
        setTimeout(() => nextTick(focus), 150);
    }
});
defineExpose({
    blur,
    focus,
    input,
});
</script>
<template>
    <mr-input-container
        :label="localLabel"
        :disabled
        :invalid="localInvalid"
        :description="localDesc"
        :size="textArea ? 'free' : size"
        :hide-description
        :flat-right
        :flat-left
        :full-width
        :success
        :resizable
        :scroll-to-container
        data-focus-enabled="true"
        @click="emit('click', $event)"
    >
        <label
            class="input-container"
            :class="{
                textarea: textArea,
                fullWidth,
                'with-icon': !!icon,
                disabled,
                invalid: localInvalid,
            }"
            @click="focus"
        >
            <mr-icon
                v-if="icon"
                class="input-icon"
                :icon="icon!"
                @click="!disabled && emit('click:icon', $event)"
            />
            <input
                v-if="!textArea"
                ref="input"
                v-model="modelValue"
                class="input"
                :class="align"
                :inputmode="inputMode"
                v-bind="computedId"
                :data-invalid-focus="localInvalid"
                :type="computedType"
                :min
                :max
                :placeholder="localPlaceholder"
                :maxlength="maxLength"
                :minlength="minLength"
                :readonly
                :autocomplete
                :disabled
                :dir
                @keypress.enter.prevent="emit('submit')"
                @keypress="emit('keypress', $event)"
                @focus="emit('focus')"
                @blur="onBlur"
                @keydown="emit('keydown', $event)"
                @search="
                    type === 'search' &&
                        emit('search', $event.target.value as string)
                "
            />
            <textarea
                v-else
                ref="input"
                v-bind="computedId"
                v-model="modelValue"
                class="input textarea"
                :data-invalid-focus="localInvalid"
                :min
                :max
                :placeholder="localPlaceholder"
                :maxlength="maxLength"
                :minlength="minLength"
                :readonly
                :autocomplete
                :disabled
                :dir
            />
            <mr-button
                v-if="showClear"
                :icon-button="faXmark"
                size="medium"
                variant="transparent"
                @click.prevent.stop="clear"
            />
            <slot />
        </label>
    </mr-input-container>
</template>

<style scoped lang="scss">
.input-container {
    flex-grow: 1;
    display: flex;
    align-items: center;
    height: 100%;
    &.with-icon {
        .input {
            padding-right: 8px;
        }
    }

    &.disabled {
        .input-icon {
            color: usePalette($p-gray, $p-500);
        }
    }

    &.invalid {
        .input-icon {
            color: usePalette($p-red, $p-200);
        }
    }

    &.textarea {
        width: fit-content;
        height: fit-content;
        &.with-icon {
            align-items: flex-start;
            padding-top: 10px;
            .input {
                padding-top: 0;
            }
        }
    }
}

.input-icon {
    margin-right: 12px;
    color: usePalette($p-cool-gray, $p-500);
}

.input {
    width: 100%;
    height: 100%;
    border: none;
    border-radius: 8px;
    padding: 0 12px;
    caret-color: usePalette($p-blue, $p-500);
    outline: none;
    background-color: transparent;
    // text
    @include set-font($important: true);
    @include tg-l($w5);
    font-size: 16px;
    text-align: start;
    &.left {
        text-align: left;
        &::placeholder {
            text-align: left;
        }
    }
    &.right {
        text-align: right;
        &::placeholder {
            text-align: right;
        }
    }
    &.center {
        text-align: center;
        &::placeholder {
            text-align: center;
        }
    }

    &.textarea {
        min-height: 140px;
        width: 100%;
        padding-top: 10px;
        resize: none;
    }
    &::placeholder {
        direction: rtl;
    }
}
</style>
